import { Language, Notifications } from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import { Badge, Divider, styled } from '@mui/material';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { AxiosError } from 'axios';
import { GB, IT, SI } from 'country-flag-icons/react/3x2';
import { format } from 'date-fns';
import I18n, { changeLanguage } from 'i18next';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { drawerWidth } from '../../router/Redirect';
import { useNotifications } from '../hooks/useNotifications';
import { IBeehiveAlert } from '../interfaces/beehiveAlerts';
import beehiveAlertsService from '../services/beehiveAlertsService';
import CustomSnackbar from './CustomSnackbar';

import { enGB } from 'date-fns/locale/en-GB';
import { it } from 'date-fns/locale/it';
import { sl } from 'date-fns/locale/sl';
import { useAuth } from '../hooks/useAuth';
import { useReload } from '../hooks/useReload';

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        width: `calc(100% - ${drawerWidth}px)`,
        marginLeft: `${drawerWidth}px`,
        transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

interface IHeaderProps {
    handleDrawer: (value: boolean) => void,
    open: boolean
}

const Header: FC<IHeaderProps> = (props) => {
    const { handleDrawer, open } = props;
    const { permissionsState } = useAuth();
    const { reload, setReload } = useReload();
    const navigate = useNavigate();
    const { setCurrentNotification, setNotifications, headerNotifications, setHeaderNotifications } = useNotifications();
    const locale = I18n.language;
    const { t } = useTranslation();
    const [language, setLanguage] = useState(locale);

    const [anchorElLang, setAnchorElLang] = useState<null | HTMLElement>(null);
    const [anchorElNotifications, setAnchorElNotifications] = useState<null | HTMLElement>(null);

    // snackbar
    const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
    const [messageSnackbar, setMessageSnackbar] = useState<string>("");
    const [responseType, setResponseType] = useState<"success" | "error">("success");

    const lang = [
        { flag: <GB title="English" />, label: I18n.t("EN", { locale: language }), value: "en-EN" },
        { flag: <IT title="Italy" />, label: I18n.t("IT", { locale: language }), value: "it-IT" },
        { flag: <SI title="Slovenian" />, label: I18n.t("SL", { locale: language }), value: "sl-SI" },
    ]

    const handleOpenLangMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElLang(event.currentTarget);
    };
    const handleOpenNotificationsMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElNotifications(event.currentTarget);
    };

    const handleCloseLangMenu = (option: { label: string, value: string }) => {
        if (locale !== option.value) {
            setLanguage(option.value)
            changeLanguage(option.value);
            setReload(true);
        }
        setAnchorElLang(null);
    };
    const handleCloseNotificationsMenu = () => {
        setAnchorElNotifications(null);
    };

    const getAlertsList = async () => {
        try {
            const response = await beehiveAlertsService.getAllGeneric(0, 10);
            setNotifications(response.content);
            setHeaderNotifications([...response.content.filter(alert => !alert.isRead).slice(0, 5)])
        }
        catch (error) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.title)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        }
    }

    useEffect(() => {
        if (
            permissionsState &&
            permissionsState.existViewAlertsPermission &&
            permissionsState.existViewBeehivesPermission
        )
            getAlertsList()
    }, [])

    const onNotificationClick = (notif: IBeehiveAlert) => {
        handleCloseNotificationsMenu();
        setCurrentNotification(notif);
        navigate("/notifications");
    }

    return (
        <>
            <CustomSnackbar
                type={responseType}
                message={messageSnackbar}
                open={openSnackbar}
                onClose={() => setOpenSnackbar(false)}
            />
            <AppBar position="sticky">
                <Container maxWidth={false}>
                    <Toolbar disableGutters>
                        <Box sx={{ display: "flex", justifyContent: "start" }}>
                            <IconButton
                                size="large"
                                aria-label="account of current user"
                                aria-controls="menu-appbar"
                                aria-haspopup="true"
                                onClick={() => handleDrawer(!open)}
                                color="primary"
                            >
                                <MenuIcon />
                            </IconButton>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "end", flexGrow: 1, py: 1 }}>
                            {
                                permissionsState &&
                                permissionsState.existViewAlertsPermission &&
                                permissionsState.existViewBeehivesPermission &&
                                <>
                                    <Tooltip title="Notifications" sx={{ mr: 1 }}>
                                        <IconButton onClick={handleOpenNotificationsMenu} color="primary">
                                            <Badge color="error" variant="dot" invisible={headerNotifications.length === 0}>
                                                <Notifications />
                                            </Badge>
                                        </IconButton>
                                    </Tooltip>
                                    <Menu
                                        sx={{ mt: '45px' }}
                                        id="menu-appbar"
                                        anchorEl={anchorElNotifications}
                                        anchorOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                        keepMounted
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                        open={Boolean(anchorElNotifications)}
                                        onClose={handleCloseNotificationsMenu}
                                    >
                                        {
                                            headerNotifications.length > 0 ? headerNotifications.map((notif) => (
                                                <MenuItem key={notif.id} onClick={() => onNotificationClick(notif)}>
                                                    <Typography textAlign="center" sx={{ mr: 2 }}>
                                                        {
                                                            format(notif.timestamp, "dd-MM-yy, HH:mm", { locale: language === "it-IT" ? it : language === "sl-SI" ? sl : enGB })
                                                        }
                                                    </Typography>
                                                    <Typography textAlign="center" sx={{ fontWeight: 500 }}>{
                                                        locale === "it-IT" ? notif.content_IT.description : notif.content_EN.description
                                                    }</Typography>
                                                </MenuItem>
                                            )) : <Typography variant="body1" sx={{ p: 2, textAlign: "center" }}>
                                                {t("header.noNotificationsLabel")}
                                            </Typography>
                                        }
                                        <Divider />
                                        <MenuItem onClick={() => { handleCloseNotificationsMenu(); navigate("/notifications"); }}>
                                            {t("header.goToNotificationsPage")}
                                        </MenuItem>
                                    </Menu>
                                </>
                            }
                            <Tooltip title={t("header.selectLanguage")}>
                                <IconButton onClick={handleOpenLangMenu} color="primary">
                                    <Language />
                                </IconButton>
                            </Tooltip>
                            <Menu
                                sx={{ mt: '45px' }}
                                id="menu-appbar"
                                anchorEl={anchorElLang}
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                keepMounted
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                open={Boolean(anchorElLang)}
                                onClose={handleCloseLangMenu}
                            >
                                {lang.map((option) => (
                                    <MenuItem key={option.label} onClick={() => handleCloseLangMenu(option)}>
                                        <Box sx={{
                                            width: 28,
                                            height: 19,
                                            mr: 1
                                        }}>{option.flag}</Box>
                                        <Typography textAlign="center">{option.label}</Typography>
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>
        </>
    );
}
export default Header;